import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../auth/service';
import { User, Question, SiteConfig } from '../types';
import ConfirmationModal from './ConfirmationModal';
import QuestionEditModal from './QuestionEditModal';
import SuccessModal from './SuccessModal';
import SimuladoManagement from './SimuladoManagement';

interface AdminScreenProps {
    onExit: () => void;
    currentConfig: SiteConfig;
    onConfigChange: (newConfig: SiteConfig) => void;
}

// Sub-component for User Management
const UserManagement: React.FC = () => {
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [newPassword, setNewPassword] = useState('');
    const [passwordError, setPasswordError] = useState<string | null>(null);
    const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);
    const [showPerformanceModal, setShowPerformanceModal] = useState(false);
    const [performanceHistory, setPerformanceHistory] = useState<any[]>([]);
    const [showSessionsModal, setShowSessionsModal] = useState(false);
    const [sessions, setSessions] = useState<any[]>([]);
    const [showEditModal, setShowEditModal] = useState<boolean>(false);
    const [editForm, setEditForm] = useState<{ firstName: string; lastName: string; email: string; enrolledSimulados: string[] }>({ firstName: '', lastName: '', email: '', enrolledSimulados: [] });
    const [availableSimulados, setAvailableSimulados] = useState<{ code: string; name: string }[]>([]);

    const fetchAvailableSimulados = async () => {
        try {
            const list = await apiClient.get('/admin/simulados');
            if (Array.isArray(list)) {
                setAvailableSimulados(list.map(s => ({ code: s.code, name: s.name })));
            }
        } catch {}
    };

    useEffect(() => {
        fetchAvailableSimulados();
    }, []);

    const formatDateTime = (isoString: string) => {
        const date = new Date(isoString);
        return date.toLocaleString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatTime = (totalSeconds: number) => {
        if (isNaN(totalSeconds) || totalSeconds < 0) return '00:00';
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    };

    const getBarColor = (percentage: number) => {
        if (percentage >= 72) return 'bg-green-500';
        if (percentage >= 50) return 'bg-yellow-500';
        return 'bg-red-500';
    };

    const fetchUsers = useCallback(async () => {
         setLoading(true);
         setError(null);
         try {
             const response = await apiClient.get('/admin/users');
             // Handle various possible response structures
             const usersArray = Array.isArray(response) ? response : response?.data || response?.users;
             if (Array.isArray(usersArray)) {
                 setUsers(usersArray);
             } else {
                 throw new Error("Formato de resposta inesperado da API de usuários.");
             }
         } catch (err: any) {
             console.error('Error fetching users:', err);
             let errorMessage = 'Ocorreu um erro desconhecido ao buscar usuários.';
 
             if (err.response) { // Axios error structure
                 switch (err.response.status) {
                     case 401:
                         errorMessage = 'Não autorizado. Por favor, faça login novamente.';
                         break;
                     case 403:
                         errorMessage = 'Acesso negado. Você não tem permissão de administrador.';
                         break;
                     case 404:
                         errorMessage = 'Endpoint de usuários não encontrado no servidor (404).';
                         break;
                     default:
                         errorMessage = err.response.data?.message || err.message;
                         break;
                 }
             } else if (err.message) {
                 errorMessage = err.message;
             }
             
             setError(errorMessage);
         } finally {
             setLoading(false);
         }
     }, []);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    const handleToggleActive = async (user: User) => {
         try {
             const endpoint = user.isActive ? `/admin/users/${user._id}/deactivate` : `/admin/users/${user._id}/activate`;
             const response = await apiClient.put(endpoint);
             setUsers(prevUsers => prevUsers.map(u => u._id === user._id ? response.user : u));
         } catch (err: any) {
             setError(err.message || 'An unknown error occurred.');
         }
     };

    const handleRoleChange = async (user: User, newRole: 'admin' | 'user') => {
        if (user.role === newRole) return;
        try {
            const response = await apiClient.put(`/admin/users/${user._id}/role`, { role: newRole });
            setUsers(prevUsers => prevUsers.map(u => u._id === user._id ? response.user : u));
        } catch (err: any) {
            let errorMessage = 'Ocorreu um erro ao alterar a função do usuário.';
            if (err.response) { // Axios error structure
                switch (err.response.status) {
                    case 400:
                        errorMessage = err.response.data?.message || 'Função inválida especificada.';
                        break;
                    case 401:
                        errorMessage = 'Não autorizado. Por favor, faça login novamente.';
                        break;
                    case 403:
                        errorMessage = 'Acesso negado. Você não tem permissão de administrador.';
                        break;
                    case 404:
                        errorMessage = 'Usuário ou endpoint não encontrado no servidor (404).';
                        break;
                    default:
                        errorMessage = err.response.data?.message || `Erro desconhecido com status: ${err.response.status}`;
                        break;
                }
            }
            setError(errorMessage);
        }
    };

    const openEditUser = (user: User): void => {
        setSelectedUser(user);
        setEditForm({ 
            firstName: user.firstName, 
            lastName: user.lastName, 
            email: user.email,
            enrolledSimulados: user.enrolledSimulados || []
        });
        setShowEditModal(true);
    };

    const handleEditUserSave = async (): Promise<void> => {
        if (!selectedUser) return;
        const { firstName, lastName, email, enrolledSimulados } = editForm;
        if (!firstName || !lastName || !email) {
            setError('Preencha Nome, Sobrenome e Email.');
            return;
        }
        try {
            const response = await apiClient.put(`/admin/users/${selectedUser._id}`, { firstName, lastName, email, enrolledSimulados });
            setUsers(prev => prev.map(u => u._id === selectedUser._id ? response.user : u));
            setShowEditModal(false);
            setSelectedUser(null);
        } catch (err: any) {
            const msg = err?.response?.data?.message || err.message || 'Falha ao atualizar usuário';
            setError(msg);
        }
    };

    const handleToggleTrial = async (user: User) => {
        try {
            const response = await apiClient.put(`/admin/users/${user._id}/trial`, { isTrial: !user.isTrial });
            setUsers(prevUsers => prevUsers.map(u => u._id === user._id ? response.user : u));
        } catch (err: any) {
            setError(err.message || 'An unknown error occurred.');
        }
    };
    const handleChangePassword = async () => {
        if (!selectedUser || !newPassword) {
            setPasswordError("Password cannot be empty.");
            return;
        }
        setPasswordError(null);
        try {
            await apiClient.put(`/admin/users/${selectedUser._id}/password`, { newPassword });
            setPasswordSuccess("Password updated successfully!");
            setShowPasswordModal(false);
            setNewPassword('');
            setSelectedUser(null);
        } catch (err: any) {
            setPasswordError(err.message || 'An unknown error occurred.');
        }
    };

    const handleDeleteUser = async () => {
        if (!selectedUser) return;
        try {
            await apiClient.delete(`/admin/users/${selectedUser._id}`);
            setUsers(prevUsers => prevUsers.filter(u => u._id !== selectedUser._id));
            setShowDeleteModal(false);
            setSelectedUser(null);
        } catch (err: any) {
            setError(err.message || 'An unknown error occurred.');
        }
    };

    const handleShowPerformance = async (user: User) => {
        try {
            setSelectedUser(user);
            const history = await apiClient.get(`/admin/users/${user._id}/history`);
            setPerformanceHistory(Array.isArray(history) ? history : []);
            setShowPerformanceModal(true);
        } catch (err: any) {
            setError(err.message || 'Falha ao carregar desempenho do usuário.');
        }
    };

    const handleShowSessions = async (user: User) => {
        try {
            setSelectedUser(user);
            const result = await apiClient.get(`/admin/users/${user._id}/sessions`);
            setSessions(Array.isArray(result) ? result : []);
            setShowSessionsModal(true);
        } catch (err: any) {
            setError(err.message || 'Falha ao carregar sessões do usuário.');
        }
    };

    return (
        <div className="mb-8">
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Gerenciar Usuários</h3>
            {loading && <p className="text-gray-600 dark:text-slate-300 mb-4">Carregando usuários...</p>}
            {error && <p className="text-red-500 mb-4">Erro: {error}</p>}
            <div className="overflow-x-auto">
                <table className="min-w-full bg-white dark:bg-slate-700 rounded-lg shadow-md">
                    <thead>
                        <tr className="bg-gray-200 dark:bg-slate-600 text-gray-600 dark:text-slate-200 uppercase text-sm leading-normal">
                            <th className="py-3 px-6 text-left">Nome</th>
                            <th className="py-3 px-6 text-left">Email</th>
                            <th className="py-3 px-6 text-center">Ativo</th>
                            <th className="py-3 px-6 text-center">Função</th>
                            <th className="py-3 px-6 text-center">Ações</th>
                        </tr>
                    </thead>
                    <tbody className="text-gray-600 dark:text-slate-300 text-sm font-light">
                        {users && Array.isArray(users) && users.length > 0 ? (
                            users.map(user => (
                                <tr key={user._id} className="border-b border-gray-200 dark:border-slate-600 hover:bg-gray-100 dark:hover:bg-slate-800">
                                    <td className="py-3 px-6 text-left whitespace-nowrap">{user.firstName} {user.lastName}</td>
                                    <td className="py-3 px-6 text-left">{user.email}</td>
                                    <td className="py-3 px-6 text-center">
                                        <span className={`relative inline-block px-3 py-1 font-semibold text-green-900 leading-tight ${user.isActive ? 'text-green-900' : 'text-red-900'}`}>
                                            <span aria-hidden className={`absolute inset-0 opacity-50 rounded-full ${user.isActive ? 'bg-green-200' : 'bg-red-200'}`}></span>
                                            <span className="relative">{user.isActive ? 'Sim' : 'Não'}</span>
                                        </span>
                                    </td>
                                    <td className="py-3 px-6 text-center">
                                        <select
                                            value={user.role}
                                            onChange={(e) => handleRoleChange(user, e.target.value as 'admin' | 'user')}
                                            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-1.5 dark:bg-slate-600 dark:border-slate-500 dark:placeholder-slate-400 dark:text-white"
                                        >
                                            <option value="user">User</option>
                                            <option value="admin">Admin</option>
                                        </select>
                                    </td>
                                    <td className="py-3 px-6 text-center">
                                    <div className="flex item-center justify-center space-x-2">
                                            <button onClick={() => handleToggleActive(user)} className={`px-2 py-1 rounded ${user.isActive ? 'bg-yellow-500 hover:bg-yellow-600' : 'bg-green-500 hover:bg-green-600'} text-white text-xs`}>
                                                {user.isActive ? 'Desativar' : 'Ativar'}
                                            </button>
                                            <button onClick={() => handleToggleTrial(user)} className={`px-2 py-1 rounded ${user.isTrial ? 'bg-blue-500 hover:bg-blue-600' : 'bg-gray-500 hover:bg-gray-600'} text-white text-xs`}>
                                                {user.isTrial ? 'Trial: ON' : 'Trial: OFF'}
                                            </button>
                                            <button onClick={() => { setSelectedUser(user); setShowPasswordModal(true); }} className="px-2 py-1 rounded bg-blue-500 hover:bg-blue-600 text-white text-xs">
                                                Senha
                                            </button>
                                            <button onClick={() => handleShowPerformance(user)} className="px-2 py-1 rounded bg-indigo-500 hover:bg-indigo-600 text-white text-xs">
                                                Desempenho
                                            </button>
                                            <button onClick={() => handleShowSessions(user)} className="px-2 py-1 rounded bg-teal-500 hover:bg-teal-600 text-white text-xs">
                                                Sessões
                                            </button>
                                            <button onClick={() => openEditUser(user)} className="px-2 py-1 rounded bg-purple-500 hover:bg-purple-600 text-white text-xs">
                                                Editar
                                            </button>
                                            <button onClick={() => { setSelectedUser(user); setShowDeleteModal(true); }} className="px-2 py-1 rounded bg-red-500 hover:bg-red-600 text-white text-xs">
                                                Excluir
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            !loading && (
                                <tr>
                                    <td colSpan={5} className="py-4 px-6 text-center text-gray-500 dark:text-slate-400">
                                        Nenhum usuário encontrado.
                                    </td>
                                </tr>
                            )
                        )}
                    </tbody>
                </table>
            </div>

            {/* Change Password Modal */}
            <ConfirmationModal
                isOpen={showPasswordModal}
                onConfirm={handleChangePassword}
                onCancel={() => { setShowPasswordModal(false); setNewPassword(''); setPasswordError(null); }}
                title={`Alterar Senha para ${selectedUser?.email}`}
                confirmText="Salvar"
            >
                <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Nova Senha"
                    className="w-full px-3 py-2 border rounded-md dark:bg-slate-800 dark:border-slate-600 dark:text-white"
                />
                {passwordError && <p className="text-red-500 text-sm mt-2">{passwordError}</p>}
            </ConfirmationModal>

            {/* Delete User Modal */}
            <ConfirmationModal
                isOpen={showDeleteModal}
                onConfirm={handleDeleteUser}
                onCancel={() => { setShowDeleteModal(false); setSelectedUser(null); }}
                title={`Excluir Usuário ${selectedUser?.email}?`}
                confirmText="Excluir"
                confirmButtonClass="bg-red-600 hover:bg-red-700"
            >
                Tem certeza de que deseja excluir o usuário {selectedUser?.email}? Esta ação é irreversível e também excluirá o histórico e o progresso do quiz.
            </ConfirmationModal>

            {/* Edit User Modal */}
            <ConfirmationModal
                isOpen={showEditModal}
                onConfirm={handleEditUserSave}
                onCancel={() => { setShowEditModal(false); setSelectedUser(null); }}
                title={`Editar Usuário ${selectedUser?.email}`}
                confirmText="Salvar"
            >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Nome</label>
                        <input
                            type="text"
                            value={editForm.firstName}
                            onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })}
                            className="w-full px-3 py-2 border rounded-md dark:bg-slate-800 dark:border-slate-600 dark:text-white"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Sobrenome</label>
                        <input
                            type="text"
                            value={editForm.lastName}
                            onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })}
                            className="w-full px-3 py-2 border rounded-md dark:bg-slate-800 dark:border-slate-600 dark:text-white"
                        />
                    </div>
                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Email</label>
                        <input
                            type="email"
                            value={editForm.email}
                            onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                            className="w-full px-3 py-2 border rounded-md dark:bg-slate-800 dark:border-slate-600 dark:text-white"
                        />
                    </div>
                    <div className="md:col-span-2 border-t dark:border-slate-700 pt-4 mt-2">
                        <label className="block text-sm font-bold text-gray-800 dark:text-white mb-2 flex items-center gap-2">
                            <i className="fas fa-check-double text-indigo-500"></i>
                            Simulados Inscritos (Permissões de Acesso)
                        </label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 bg-gray-50 dark:bg-slate-900/50 p-4 rounded-lg border-2 border-dashed border-gray-200 dark:border-slate-700 max-h-48 overflow-y-auto">
                            {availableSimulados.length > 0 ? (
                                availableSimulados.map(sim => (
                                    <label key={sim.code} className="flex items-center gap-3 cursor-pointer hover:bg-white dark:hover:bg-slate-800 p-2 rounded-md transition-all shadow-sm">
                                        <input
                                            type="checkbox"
                                            checked={editForm.enrolledSimulados.includes(sim.code)}
                                            onChange={(e) => {
                                                const codes = e.target.checked
                                                    ? [...editForm.enrolledSimulados, sim.code]
                                                    : editForm.enrolledSimulados.filter(c => c !== sim.code);
                                                setEditForm({ ...editForm, enrolledSimulados: codes });
                                            }}
                                            className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                        />
                                        <div className="flex flex-col">
                                            <span className="text-sm font-semibold text-gray-700 dark:text-slate-200 truncate max-w-[150px]" title={sim.name}>
                                                {sim.name}
                                            </span>
                                            <span className="text-[10px] text-gray-500 font-mono">{sim.code}</span>
                                        </div>
                                    </label>
                                ))
                            ) : (
                                <div className="col-span-full py-4 text-center">
                                    <p className="text-sm text-gray-500 italic">Carregando simulados ou nenhum cadastrado...</p>
                                    <button onClick={fetchAvailableSimulados} className="text-xs text-indigo-500 hover:underline mt-2">Tentar recarregar</button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </ConfirmationModal>

            <SuccessModal
                isOpen={!!passwordSuccess}
                title="Sucesso!"
                onConfirm={() => setPasswordSuccess(null)}
                buttonText="Ok"
            >
                {passwordSuccess}
            </SuccessModal>

            <ConfirmationModal
                isOpen={showPerformanceModal}
                onConfirm={() => setShowPerformanceModal(false)}
                onCancel={() => setShowPerformanceModal(false)}
                title={`Desempenho de ${selectedUser?.email}`}
                confirmText="Fechar"
                confirmButtonClass="bg-indigo-600 hover:bg-indigo-700"
                maxWidthClass="max-w-[1280px]"
            >
                {performanceHistory && performanceHistory.length > 0 ? (
                    <div className="space-y-6 text-left">
                        {(() => {
                            const domainStats: Record<string, { correct: number; total: number }> = {};
                            performanceHistory.forEach((entry: any) => {
                                (entry.questions || []).forEach((q: any) => {
                                    const domain = q.domain || 'Não categorizado';
                                    if (!domainStats[domain]) domainStats[domain] = { correct: 0, total: 0 };
                                    domainStats[domain].total++;
                                    const ans = entry.answers?.[q.id];
                                    if (ans?.isCorrect) domainStats[domain].correct++;
                                });
                            });
                            const perfArr = Object.entries(domainStats).map(([domain, s]) => ({ domain, ...s, percentage: s.total ? Math.round((s.correct / s.total) * 100) : 0 }));
                            if (perfArr.length === 0) return null;
                            const sorted = [...perfArr].sort((a, b) => a.percentage - b.percentage);
                            const weakest = sorted[0];
                            const strongest = sorted[sorted.length - 1];
                            return (
                                <div className="bg-white dark:bg-slate-800 p-6 rounded-lg shadow-2xl">
                                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Resumo Geral</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                                        <div className="bg-gray-50 dark:bg-slate-900/50 p-4 rounded-lg text-center">
                                            <h4 className="text-lg font-semibold text-green-400 mb-2">Ponto Mais Forte</h4>
                                            <p className="text-xl font-bold">{strongest.domain}</p>
                                            <p className="text-gray-600 dark:text-slate-400">{strongest.percentage}% de acerto</p>
                                        </div>
                                        <div className="bg-gray-50 dark:bg-slate-900/50 p-4 rounded-lg text-center">
                                            <h4 className="text-lg font-semibold text-red-400 mb-2">Área para Focar</h4>
                                            <p className="text-xl font-bold">{weakest.domain}</p>
                                            <p className="text-gray-600 dark:text-slate-400">{weakest.percentage}% de acerto</p>
                                        </div>
                                    </div>
                                    <div>
                                        <h4 className="text-lg font-semibold text-gray-800 dark:text-white mb-3">Desempenho por Domínio</h4>
                                        <div className="space-y-4">
                                            {perfArr.map((p) => (
                                                <div key={p.domain}>
                                                    <div className="flex justify-between items-center mb-1 text-gray-700 dark:text-slate-300">
                                                        <span>{p.domain}</span>
                                                        <span>{p.correct}/{p.total} ({p.percentage}%)</span>
                                                    </div>
                                                    <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-2.5">
                                                        <div className={`${getBarColor(p.percentage)} h-2.5 rounded-full`} style={{ width: `${p.percentage}%` }}></div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            );
                        })()}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {(() => {
                                const domainStats: Record<string, { correct: number; total: number }> = {};
                                performanceHistory.forEach((entry: any) => {
                                    (entry.questions || []).forEach((q: any) => {
                                        const domain = q.domain || 'Não categorizado';
                                        if (!domainStats[domain]) domainStats[domain] = { correct: 0, total: 0 };
                                        domainStats[domain].total++;
                                        const ans = entry.answers?.[q.id];
                                        if (ans?.isCorrect) domainStats[domain].correct++;
                                    });
                                });
                                const perfArr = Object.entries(domainStats).map(([domain, s]) => ({ domain, ...s, percentage: s.total ? Math.round((s.correct / s.total) * 100) : 0 }));
                                if (perfArr.length === 0) return null;
                                const sorted = [...perfArr].sort((a, b) => a.percentage - b.percentage);
                                const weakest = sorted[0];
                                const strongest = sorted[sorted.length - 1];
                                return (
                                    <>
                                        <div className="bg-gray-50 dark:bg-slate-900/50 p-4 rounded-lg text-center">
                                            <h4 className="text-lg font-semibold text-green-400 mb-2">Ponto Mais Forte</h4>
                                            <p className="text-xl font-bold">{strongest.domain}</p>
                                            <p className="text-gray-600 dark:text-slate-400">{strongest.percentage}% de acerto</p>
                                        </div>
                                        <div className="bg-gray-50 dark:bg-slate-900/50 p-4 rounded-lg text-center">
                                            <h4 className="text-lg font-semibold text-red-400 mb-2">Área para Focar</h4>
                                            <p className="text-xl font-bold">{weakest.domain}</p>
                                            <p className="text-gray-600 dark:text-slate-400">{weakest.percentage}% de acerto</p>
                                        </div>
                                    </>
                                );
                            })()}
                        </div>

                        <div className="mt-6">
                            <h4 className="text-lg font-semibold text-gray-800 dark:text-white mb-3">Desempenho por Domínio</h4>
                            <div className="space-y-4">
                                {(() => {
                                    const domainStats: Record<string, { correct: number; total: number }> = {};
                                    performanceHistory.forEach((entry: any) => {
                                        (entry.questions || []).forEach((q: any) => {
                                            const domain = q.domain || 'Não categorizado';
                                            if (!domainStats[domain]) domainStats[domain] = { correct: 0, total: 0 };
                                            domainStats[domain].total++;
                                            const ans = entry.answers?.[q.id];
                                            if (ans?.isCorrect) domainStats[domain].correct++;
                                        });
                                    });
                                    const perfArr = Object.entries(domainStats).map(([domain, s]) => ({ domain, ...s, percentage: s.total ? Math.round((s.correct / s.total) * 100) : 0 }));
                                    return perfArr.map((p) => (
                                        <div key={p.domain}>
                                            <div className="flex justify-between items-center mb-1 text-gray-700 dark:text-slate-300">
                                                <span>{p.domain}</span>
                                                <span>{p.correct}/{p.total} ({p.percentage}%)</span>
                                            </div>
                                            <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-2.5">
                                                <div className={`${getBarColor(p.percentage)} h-2.5 rounded-full`} style={{ width: `${p.percentage}%` }}></div>
                                            </div>
                                        </div>
                                    ));
                                })()}
                            </div>
                        </div>

                        <div>
                            <h4 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Histórico de Tentativas</h4>
                            <div className="space-y-4">
                                {performanceHistory.map((h) => {
                                    const passScore = typeof h.passingScore === 'number' && h.passingScore > 0 ? h.passingScore : 720;
                                    const didPass = Number(h.score) >= passScore;
                                    const correctCount = Object.values(h.answers || {}).filter((a: any) => a.isCorrect).length;
                                    const total = (h.questions || []).length;
                                    const modeText = h.customConfig ? 'Personalizado' : (h.mode === 'practice' ? 'Modo Prática' : 'Modo Simulado');
                                    const modeColor = h.customConfig ? 'text-purple-400' : (h.mode === 'practice' ? 'text-orange-400' : 'text-cyan-400');
                                    return (
                                        <div key={h._id} className="bg-white dark:bg-slate-800 p-4 rounded-lg shadow-lg flex flex-col sm:flex-row items-center justify-between gap-4">
                                            <div className="flex-1 text-center sm:text-left">
                                                <div className="text-sm text-gray-500 dark:text-slate-400">{formatDateTime(h.date)}</div>
                                                <div className={`font-bold text-lg ${modeColor}`}>{modeText}</div>
                                            </div>
                                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 flex-grow-[2] text-center">
                                                <div>
                                                    <div className="text-xs text-gray-500 dark:text-slate-400">Corretas</div>
                                                    <div className="font-bold text-lg text-green-400">{correctCount}/{total}</div>
                                                </div>
                                                <div>
                                                    <div className="text-xs text-gray-500 dark:text-slate-400">Tempo</div>
                                                    <div className="font-bold text-lg font-mono">{formatTime(Number(h.timeTaken || 0))}</div>
                                                </div>
                                                <div>
                                                    <div className="text-xs text-gray-500 dark:text-slate-400">Status</div>
                                                    <div className={`font-bold text-lg ${didPass ? 'text-green-400' : 'text-red-400'}`}>{didPass ? 'Aprovado' : 'Reprovado'}</div>
                                                </div>
                                                <div>
                                                    <div className="text-xs text-gray-500 dark:text-slate-400">Pontuação</div>
                                                    <div className={`font-bold text-2xl ${didPass ? 'text-green-400' : 'text-red-400'}`}>{h.score}</div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                ) : (
                    <p className="text-gray-600 dark:text-slate-300">Nenhum histórico disponível.</p>
                )}
            </ConfirmationModal>

            <ConfirmationModal
                isOpen={showSessionsModal}
                onConfirm={() => setShowSessionsModal(false)}
                onCancel={() => setShowSessionsModal(false)}
                title={`Sessões de Login de ${selectedUser?.email}`}
                confirmText="Fechar"
                confirmButtonClass="bg-teal-600 hover:bg-teal-700"
                maxWidthClass="max-w-[1280px]"
            >
                {sessions && sessions.length > 0 ? (
                    <div className="space-y-3 text-left">
                        {sessions.map((s) => (
                            <div key={s._id} className="p-3 rounded bg-white dark:bg-slate-800 shadow">
                                <div className="text-sm text-gray-700 dark:text-slate-300">
                                    <span className="font-semibold">Login:</span> {new Date(s.loginAt).toLocaleString()}
                                </div>
                                <div className="text-sm text-gray-700 dark:text-slate-300">
                                    <span className="font-semibold">Logout:</span> {s.logoutAt ? new Date(s.logoutAt).toLocaleString() : 'Ativo'}
                                </div>
                                <div className="text-sm text-gray-700 dark:text-slate-300">
                                    <span className="font-semibold">Duração:</span> {typeof s.durationSeconds === 'number' ? `${Math.floor(s.durationSeconds/60)} min ${s.durationSeconds%60} s` : '—'}
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="text-gray-600 dark:text-slate-300">Nenhuma sessão registrada.</p>
                )}
            </ConfirmationModal>
        </div>
    );
};

// Sub-component for Question Management
const QuestionManagement: React.FC = () => {
    const [questions, setQuestions] = useState<Question[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showQuestionModal, setShowQuestionModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
    const [questionForm, setQuestionForm] = useState<Partial<Question>>({});
    
    const [availableSimulados, setAvailableSimulados] = useState<{ code: string, name: string }[]>([]);
    const [selectedSimCode, setSelectedSimCode] = useState<string>(() => {
        return sessionStorage.getItem('selectedSimCode') || 'default';
    });

    const fetchQuestions = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await apiClient.get('/admin/questions', { headers: { 'x-simulado': selectedSimCode } });
            const data = Array.isArray(response) ? response : (response?.data || response?.questions || []);
            const sorted = [...data].sort((a, b) => a.id - b.id);
            setQuestions(sorted);
        } catch (err: any) {
            setError(err.message || 'An unknown error occurred.');
        } finally {
            setLoading(false);
        }
    }, [selectedSimCode]);

    const fetchSimulados = async () => {
        try {
            const list = await apiClient.get('/admin/simulados');
            if (Array.isArray(list)) {
                setAvailableSimulados(list.map(s => ({ code: s.code, name: s.name })));
            }
        } catch {}
    };

    useEffect(() => {
        fetchSimulados();
    }, []);

    useEffect(() => {
        fetchQuestions();
    }, [fetchQuestions]);

    const handleQuestionFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        if (name.startsWith('explanation.')) {
            const [_, subField] = name.split('.');
            setQuestionForm(prev => ({
                ...prev,
                explanation: {
                    ...(prev.explanation || { general: '', options: {} }),
                    [subField]: value
                }
            }));
        } else {
            setQuestionForm(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleOptionsChange = (key: string, value: string) => {
        setQuestionForm(prev => ({
            ...prev,
            options: { ...(prev.options || {}), [key]: value }
        }));
    };

    const handleExplanationOptionsChange = (key: string, value: string) => {
        setQuestionForm(prev => ({
            ...prev,
            explanation: {
                ...(prev.explanation || { general: '', options: {} }),
                options: { ...(prev.explanation?.options || {}), [key]: value }
            }
        }));
    };

    const handleCorrectAnswersChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value, checked } = e.target;
        setQuestionForm(prev => {
            const currentCorrect = new Set(prev.correctAnswers || []);
            if (checked) {
                currentCorrect.add(value);
            } else {
                currentCorrect.delete(value);
            }
            return { ...prev, correctAnswers: Array.from(currentCorrect) };
        });
    };

    const handleSaveQuestion = async () => {
        try {
            const opts = { headers: { 'x-simulado': selectedSimCode } };
            // Removemos _id e __v para evitar erros no MongoDB ao atualizar
            const { _id, __v, ...payload } = questionForm as any;
            
            if (editingQuestion) {
                await apiClient.put(`/admin/questions/${editingQuestion.id}`, payload, opts);
            } else {
                const nextId = questions.length > 0 ? Math.max(...questions.map(q => q.id)) + 1 : 1;
                await apiClient.post('/admin/questions', { ...payload, id: nextId }, opts);
            }
            setShowQuestionModal(false);
            setEditingQuestion(null);
            setQuestionForm({});
            fetchQuestions();
            alert('Questão salva com sucesso!');
        } catch (err: any) {
            setError(err.message || 'Falha ao salvar questão');
        }
    };

    const handleDeleteQuestion = async () => {
        if (!editingQuestion) return;
        try {
            await apiClient.delete(`/admin/questions/${editingQuestion.id}`, { headers: { 'x-simulado': selectedSimCode } });
            setShowDeleteModal(false);
            setEditingQuestion(null);
            fetchQuestions();
        } catch (err: any) {
            setError(err.message || 'Falha ao excluir questão');
        }
    };

    return (
        <div>
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white">Gerenciar Questões</h3>
                <div className="flex items-center gap-3 bg-indigo-50 dark:bg-slate-700/50 p-2 rounded-lg border border-indigo-100 dark:border-slate-600 w-full md:w-auto">
                    <label className="text-sm font-bold text-indigo-700 dark:text-indigo-400 whitespace-nowrap">Simulado:</label>
                    <select 
                        value={selectedSimCode} 
                        onChange={(e) => setSelectedSimCode(e.target.value)}
                        className="bg-white dark:bg-slate-800 border rounded px-3 py-1 text-sm focus:ring-2 focus:ring-indigo-500 w-full"
                    >
                        <option value="default">Padrão (Global)</option>
                        {availableSimulados.map(s => (
                            <option key={s.code} value={s.code}>{s.name}</option>
                        ))}
                    </select>
                </div>
            </div>
            {loading && <p className="text-gray-600 dark:text-slate-300 mb-4 animate-pulse italic">Carregando questões do simulado <span className="font-bold">{selectedSimCode}</span>...</p>}
            {error && <p className="text-red-500 mb-4">Erro: {error}</p>}
            <button
                onClick={() => { setEditingQuestion(null); setQuestionForm({ options: { A: '', B: '', C: '', D: '' }, correctAnswers: [], isMultiChoice: false, explanation: { general: '', options: {} } }); setShowQuestionModal(true); }}
                className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-lg mb-4 flex items-center gap-2"
            >
                <i className="fas fa-plus"></i>
                Adicionar Questão ao Simulado {selectedSimCode}
            </button>
            <div className="overflow-x-auto">
                <table className="min-w-full bg-white dark:bg-slate-700 rounded-lg shadow-md">
                    <thead>
                        <tr className="bg-gray-200 dark:bg-slate-600 text-gray-600 dark:text-slate-200 uppercase text-sm leading-normal">
                            <th className="py-3 px-6 text-left">ID</th>
                            <th className="py-3 px-6 text-left">Texto da Questão</th>
                            <th className="py-3 px-6 text-left">Domínio</th>
                            <th className="py-3 px-6 text-center">Ações</th>
                        </tr>
                    </thead>
                    <tbody className="text-gray-600 dark:text-slate-300 text-sm font-light">
                        {questions && Array.isArray(questions) && questions.length > 0 ? (
                            questions.map(q => (
                                <tr key={q.id} className="border-b border-gray-200 dark:border-slate-600 hover:bg-gray-100 dark:hover:bg-slate-800">
                                    <td className="py-3 px-6 text-left">{q.id}</td>
                                    <td className="py-3 px-6 text-left max-w-xs overflow-hidden text-ellipsis whitespace-nowrap">{q.text}</td>
                                    <td className="py-3 px-6 text-left">{q.domain || 'N/A'}</td>
                                    <td className="py-3 px-6 text-center">
                                        <div className="flex item-center justify-center space-x-2">
                                            <button
                                                onClick={() => { setEditingQuestion(q); setQuestionForm(q); setShowQuestionModal(true); }}
                                                className="px-2 py-1 rounded bg-blue-500 hover:bg-blue-600 text-white text-xs"
                                            >
                                                Editar
                                            </button>
                                            <button
                                                onClick={() => { setEditingQuestion(q); setShowDeleteModal(true); }}
                                                className="px-2 py-1 rounded bg-red-500 hover:bg-red-600 text-white text-xs"
                                            >
                                                Excluir
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            !loading && (
                                <tr>
                                    <td colSpan={4} className="py-4 px-6 text-center text-gray-500 dark:text-slate-400">
                                        Nenhuma questão encontrada.
                                    </td>
                                </tr>
                            )
                        )}
                    </tbody>
                </table>
            </div>

            {/* Add/Edit Question Modal */}
            <QuestionEditModal
                isOpen={showQuestionModal}
                onConfirm={handleSaveQuestion}
                onCancel={() => { setShowQuestionModal(false); setEditingQuestion(null); setQuestionForm({}); }}
                title={editingQuestion ? `Editar Questão ${editingQuestion.id}` : 'Adicionar Nova Questão'}
                confirmText="Salvar"
                confirmButtonClass="bg-blue-600 hover:bg-blue-700"
            >
                <div className="space-y-4">
                    {/* Imagem da Questão */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Link de Imagem (imgLink)</label>
                        <input 
                            type="text" 
                            name="imgLink" 
                            value={questionForm.imgLink || ''} 
                            onChange={handleQuestionFormChange} 
                            className="w-full px-3 py-2 border rounded-md dark:bg-slate-800 dark:border-slate-600 dark:text-white text-sm" 
                        />
                        {questionForm.imgLink && (
                            <div className="mt-3">
                                <img src={questionForm.imgLink} alt="Preview da imagem" className="max-h-48 w-auto rounded border border-gray-200 dark:border-slate-700" />
                            </div>
                        )}
                    </div>

                    {/* Texto da Questão */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Texto da Questão</label>
                        <textarea 
                            name="text" 
                            value={questionForm.text || ''} 
                            onChange={handleQuestionFormChange} 
                            rows={3}
                            className="w-full px-3 py-2 border rounded-md dark:bg-slate-800 dark:border-slate-600 dark:text-white resize-y"
                        ></textarea>
                    </div>

                    {/* Opções em Grid */}
                    <div className="grid grid-cols-2 gap-3">
                        {['A', 'B', 'C', 'D'].map(optionKey => (
                            <div key={optionKey}>
                                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Opção {optionKey}</label>
                                <input 
                                    type="text" 
                                    value={questionForm.options?.[optionKey] || ''} 
                                    onChange={(e) => handleOptionsChange(optionKey, e.target.value)} 
                                    className="w-full px-3 py-2 border rounded-md dark:bg-slate-800 dark:border-slate-600 dark:text-white text-sm" 
                                />
                            </div>
                        ))}
                    </div>


                    {/* Respostas Corretas e Múltipla Escolha em linha */}
                    <div className="flex items-center justify-between gap-4">
                        <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">Respostas Corretas</label>
                            <div className="flex space-x-4">
                                {['A', 'B', 'C', 'D'].map(optionKey => (
                                    <label key={optionKey} className="inline-flex items-center">
                                        <input
                                            type="checkbox"
                                            value={optionKey}
                                            checked={questionForm.correctAnswers?.includes(optionKey) || false}
                                            onChange={handleCorrectAnswersChange}
                                            className="form-checkbox h-5 w-5 text-orange-600"
                                        />
                                        <span className="ml-2 text-gray-700 dark:text-slate-300">{optionKey}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">Múltipla Escolha?</label>
                            <input
                                type="checkbox"
                                name="isMultiChoice"
                                checked={questionForm.isMultiChoice || false}
                                onChange={(e) => setQuestionForm(prev => ({ ...prev, isMultiChoice: e.target.checked }))}
                                className="form-checkbox h-5 w-5 text-orange-600"
                            />
                        </div>
                    </div>

                    {/* Explicação Geral */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Explicação Geral</label>
                        <textarea 
                            name="explanation.general" 
                            value={questionForm.explanation?.general || ''} 
                            onChange={handleQuestionFormChange} 
                            rows={2}
                            className="w-full px-3 py-2 border rounded-md dark:bg-slate-800 dark:border-slate-600 dark:text-white resize-y"
                        ></textarea>
                    </div>

                    {/* Explicações das Opções em Grid */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">Explicações por Opção</label>
                        <div className="grid grid-cols-2 gap-3">
                            {['A', 'B', 'C', 'D'].map(optionKey => (
                                <div key={`exp-${optionKey}`}>
                                    <label className="block text-xs font-medium text-gray-600 dark:text-slate-400 mb-1">Explicação {optionKey}</label>
                                    <input 
                                        type="text" 
                                        value={questionForm.explanation?.options?.[optionKey] || ''} 
                                        onChange={(e) => handleExplanationOptionsChange(optionKey, e.target.value)} 
                                        className="w-full px-3 py-2 border rounded-md dark:bg-slate-800 dark:border-slate-600 dark:text-white text-sm" 
                                    />
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Domínio e Link em linha */}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Domínio</label>
                            {(() => {
                                const uniqueDomains = Array.from(new Set((questions || []).map(q => q.domain).filter((d): d is string => !!d)));
                                if (uniqueDomains.length > 0) {
                                    return (
                                        <select
                                            name="domain"
                                            value={questionForm.domain || ''}
                                            onChange={handleQuestionFormChange}
                                            className="w-full px-3 py-2 border rounded-md dark:bg-slate-800 dark:border-slate-600 dark:text-white text-sm bg-white dark:bg-slate-800"
                                        >
                                            <option value="">Selecione um domínio</option>
                                            {uniqueDomains.map((d) => (
                                                <option key={d} value={d}>{d}</option>
                                            ))}
                                        </select>
                                    );
                                }
                                return (
                                    <input
                                        type="text"
                                        name="domain"
                                        value={questionForm.domain || ''}
                                        onChange={handleQuestionFormChange}
                                        className="w-full px-3 py-2 border rounded-md dark:bg-slate-800 dark:border-slate-600 dark:text-white text-sm"
                                    />
                                );
                            })()}
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Link da Documentação</label>
                            <input 
                                type="text" 
                                name="docLink" 
                                value={questionForm.docLink || ''} 
                                onChange={handleQuestionFormChange} 
                                className="w-full px-3 py-2 border rounded-md dark:bg-slate-800 dark:border-slate-600 dark:text-white text-sm" 
                            />
                        </div>
                    </div>
                </div>
            </QuestionEditModal>

            {/* Delete Question Modal */}
            <ConfirmationModal
                isOpen={showDeleteModal}
                onConfirm={handleDeleteQuestion}
                onCancel={() => { setShowDeleteModal(false); setEditingQuestion(null); }}
                title={`Excluir Questão ${editingQuestion?.id}?`}
                confirmText="Excluir"
                confirmButtonClass="bg-red-600 hover:bg-red-700"
            >
                Tem certeza de que deseja excluir a questão {editingQuestion?.id}? Esta ação é irreversível.
            </ConfirmationModal>
        </div>
    );
};

const AdminScreen: React.FC<AdminScreenProps> = ({ onExit, currentConfig, onConfigChange }) => {
    return (
        <div className="w-full max-w-7xl mx-auto animate-fade-in">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Painel de Administração</h2>
                <button onClick={onExit} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg transition-colors">
                    <i className="fas fa-arrow-left mr-2"></i>
                    Voltar
                </button>
            </div>

            <div className="bg-white dark:bg-slate-800 p-6 rounded-lg shadow-2xl">
                <UserManagement />
                <hr className="my-8 border-gray-200 dark:border-slate-700" />
                <SimuladoManagement currentConfig={currentConfig} onConfigChange={onConfigChange} />
                <hr className="my-8 border-gray-200 dark:border-slate-700" />
                <QuestionManagement />
                <hr className="my-8 border-gray-200 dark:border-slate-700" />
                <BackupRestorePanel />
            </div>
        </div>
    );
};

export default AdminScreen;
const BackupRestorePanel: React.FC = () => {
    const [downloading, setDownloading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [dbBusy, setDbBusy] = useState(false);

    const handleBackup = async () => {
        setError(null); setSuccess(null); setDownloading(true);
        try {
            const data = await apiClient.get('/admin/backup');
            const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const stamp = new Date().toISOString().replace(/[:.]/g, '-');
            a.download = `backup-${stamp}.json`;
            a.click();
            URL.revokeObjectURL(url);
            setSuccess('Backup gerado e baixado com sucesso');
        } catch (e: any) {
            setError(e?.message || 'Falha ao gerar backup');
        } finally {
            setDownloading(false);
        }
    };

    const handleRestoreFile = async (file: File) => {
        setError(null); setSuccess(null); setUploading(true);
        try {
            const text = await file.text();
            let parsed: any;
            try { parsed = JSON.parse(text); } catch { throw new Error('Arquivo JSON inválido'); }
            const payload = parsed?.data ? parsed : { data: parsed };
            const result = await apiClient.post('/admin/restore', payload);
            setSuccess('Restauração concluída');
        } catch (e: any) {
            setError(e?.message || 'Falha ao restaurar');
        } finally {
            setUploading(false);
        }
    };

    return (
        <div>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Backup e Restauração</h3>
            {error && <p className="text-red-500 mb-4">Erro: {error}</p>}
            {success && <p className="text-green-500 mb-4">{success}</p>}
            <div className="flex items-center gap-3 mb-4">
                <button onClick={handleBackup} disabled={downloading} className={`px-4 py-2 rounded text-white ${downloading ? 'bg-slate-500' : 'bg-indigo-600 hover:bg-indigo-700'}`}>Baixar Backup (.json)</button>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                    <span className="px-4 py-2 rounded text-white bg-green-600 hover:bg-green-700">Restaurar (.json)</span>
                    <input type="file" accept=".json" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleRestoreFile(f); }} />
                </label>
            </div>
            <div className="mt-6">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Nível Banco (estrutura + índices + dados)</h4>
                <div className="flex items-center gap-3">
                    <button onClick={async () => {
                        setError(null); setSuccess(null); setDbBusy(true);
                        try {
                            const data = await apiClient.get('/admin/db/backup');
                            const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url; a.download = `db-backup-${new Date().toISOString().replace(/[:.]/g, '-')}.json`;
                            a.click(); URL.revokeObjectURL(url);
                            setSuccess('Backup de banco gerado e baixado');
                        } catch (e: any) { setError(e?.message || 'Falha no backup de banco'); } finally { setDbBusy(false); }
                    }} disabled={dbBusy} className={`px-4 py-2 rounded text-white ${dbBusy ? 'bg-slate-500' : 'bg-indigo-600 hover:bg-indigo-700'}`}>Baixar Backup de Banco (.json)</button>
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                        <span className="px-4 py-2 rounded text-white bg-green-600 hover:bg-green-700">Restaurar Banco (.json)</span>
                        <input type="file" accept=".json" className="hidden" onChange={async (e) => {
                            const f = e.target.files?.[0]; if (!f) return;
                            setError(null); setSuccess(null); setDbBusy(true);
                            try {
                                const text = await f.text();
                                const parsed = JSON.parse(text);
                                await apiClient.post('/admin/db/restore', parsed);
                                setSuccess('Restauração de banco concluída');
                            } catch (e: any) { setError(e?.message || 'Falha na restauração de banco'); } finally { setDbBusy(false); }
                        }} />
                    </label>
                </div>
            </div>
        </div>
    );
};
